import { useCreateRun, useCreditPrice, useCreditShortfall } from '../../api/hooks';
import { pricedLabel } from '../../lib/copy';
import { formatDay } from '../../lib/format';
import { ErrorLine } from '../ui/error-line';
import { WorkingButton } from '../ui/working-button';
import { Button } from '../ui/button';
import { UpdateBriefButton } from './update-brief';

/**
 * The Refresh modal (ui-design §4.7), once a person has a dossier. It is a
 * **run-level** affordance, not a per-section one — facts carry no facet, so a
 * section head cannot honestly launch facet work.
 *
 * First, free: *Update the brief*, a `prose_refresh` that rewrites the brief and
 * its readings from the record and the office's notes, with no searches. It was
 * a debug tool shown to one account until the office asked to fold new notes
 * into a finished dossier without paying for research again.
 *
 * Then two priced actions (dossier Re-run and Refresh spec §3.1):
 *
 *  - *Re-run research* — the same body as the first "Run research": the person's
 *    depth, from scratch, merged into the same record, and the brief rewritten
 *    over all of it (`person_research`).
 *  - *Refresh* — every facet the depth plans, each as an update since the last
 *    dossier (`recheck_since`): only what is new, folded in (`research_recheck`).
 *
 * Open questions disable neither priced action: new findings are not prose
 * written before the answers. Each action owns its own mutation, so a spinner or
 * an error line lands under the button that caused it, and each is disabled on
 * its own shortfall.
 */
export function DossierRefresh({
  subjectId,
  since,
  updateBlocker,
  hasClubFit,
  onClose,
}: {
  subjectId: string;
  since: string;
  /** Why *Update the brief* cannot start yet (`updateBriefBlocker`), or null. */
  updateBlocker: string | null;
  /** Whether the person has a club fit reading, which the update rewrites by name. */
  hasClubFit: boolean;
  onClose: () => void;
}) {
  const rerun = useCreateRun(subjectId);
  const refresh = useCreateRun(subjectId);
  const rerunPrice = useCreditPrice('person_research');
  const refreshPrice = useCreditPrice('research_recheck');
  const rerunShort = useCreditShortfall(rerunPrice.units);
  const refreshShort = useCreditShortfall(refreshPrice.units);

  const priced = (units: number | null | undefined) =>
    units === undefined
      ? {}
      : { admission_key: crypto.randomUUID(), expected_credit_units: units ?? 0 };

  return (
    <>
      <div className="modal-line">
        Rewrite the brief{hasClubFit ? ' and club fit' : ''} from the record and your notes. No new
        searches.
      </div>

      <div className="modal-actions">
        <UpdateBriefButton subjectId={subjectId} blocker={updateBlocker} onStarted={onClose} />
      </div>

      <div className="modal-line parted">Research again from scratch and rewrite the brief.</div>

      {rerun.isError ? <ErrorLine error={rerun.error} /> : null}

      <div className="modal-actions">
        <WorkingButton
          label={pricedLabel('Research again', rerunPrice.units, rerunPrice.unitsPerCredit)}
          workingLabel="Starting…"
          working={rerun.isPending}
          disabled={rerunShort !== null || refresh.isPending}
          onClick={() =>
            rerun.mutate({ kind: 'research', ...priced(rerunPrice.units) }, { onSuccess: onClose })
          }
        />
        {rerunShort ? <span className="credit-short">{rerunShort}</span> : null}
      </div>

      <div className="modal-line parted">Look for anything new since {formatDay(since)}.</div>

      {refresh.isError ? <ErrorLine error={refresh.error} /> : null}

      <div className="modal-actions">
        <WorkingButton
          label={pricedLabel('Refresh', refreshPrice.units, refreshPrice.unitsPerCredit)}
          workingLabel="Starting…"
          working={refresh.isPending}
          disabled={refreshShort !== null || rerun.isPending}
          onClick={() =>
            refresh.mutate(
              { kind: 'research', config: { recheck_since: since }, ...priced(refreshPrice.units) },
              { onSuccess: onClose },
            )
          }
        />
        {refreshShort ? <span className="credit-short">{refreshShort}</span> : null}
        <Button variant="act" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </>
  );
}
